// src/app/news-events/page.tsx - Converted to use centralized theming system
'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Chip,
  Button,
  Tab,
  Tabs,
  Avatar,
  Skeleton,
  Pagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  colors,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CalendarToday,
  LocationOn,
  Person,
  Search,
  FilterList,
  OpenInNew,
  Event as EventIcon,
  Article as ArticleIcon,
  AccessTime,
  Visibility,
} from '@mui/icons-material';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

// Import centralized types and hooks
import type { 
  NewsArticle, 
  Event,
  NewsCategory,
  EventType,
  NewsArticleClickHandler 
} from '@/types';
import { useNewsArticles, useEvents } from '@/hooks/useApi';
import { getMediaUrl, formatDate } from '@/lib/api';

// Import our modular color system
import { getActiveColorTheme } from '@/lib/colors';

// =====================
// STYLED COMPONENTS
// =====================

const HeroSection = styled(Box)(() => {
  const colors = getActiveColorTheme();
  return {
    minHeight: '40vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    background: colors.background.hero,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("/api/placeholder/1920/600") center/cover',
      opacity: 0.2,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: colors.effects.overlay,
      zIndex: 2,
    }
  };
});

const HeroContent = styled(Box)(() => {
  const colors = getActiveColorTheme();
  return {
    position: 'relative',
    zIndex: 3,
    color: colors.text.primary,
    textAlign: 'center',
  };
});

const FilterContainer = styled(Paper)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.secondary}`,
    padding: '24px',
    marginBottom: '32px',
  };
});

const NewsCard = styled(Card)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'hidden',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-4px)',
      boxShadow: `0 12px 40px ${colors.effects.shadow}`,
    },
  };
});

const EventCard = styled(Card)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    marginBottom: '16px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateX(4px)',
      boxShadow: `0 8px 25px ${colors.effects.shadow}`,
    },
    
    '&.past-event': {
      opacity: 0.7,
      borderColor: colors.border.secondary,
      
      '&:hover': {
        opacity: 1,
        borderColor: colors.border.accent,
      },
    },
  };
});

const CategoryChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'category',
})<{ category: NewsCategory }>(({ category }) => {
  const colors = getActiveColorTheme();
  
  const getColors = (cat: NewsCategory) => {
    switch (cat) {
      case 'News': return { bg: 'rgba(33, 150, 243, 0.2)', color: '#42a5f5', border: '#42a5f5' };
      case 'Announcement': return { bg: colors.effects.glow, color: colors.brand.accent.main, border: colors.brand.accent.main };
      case 'Media Coverage': return { bg: 'rgba(156, 39, 176, 0.2)', color: '#ab47bc', border: '#ab47bc' };
      case 'Newsletter': return { bg: 'rgba(76, 175, 80, 0.2)', color: '#66bb6a', border: '#66bb6a' };
      default: return { bg: 'rgba(158, 158, 158, 0.2)', color: '#bdbdbd', border: '#bdbdbd' };
    }
  };
  
  const categoryColors = getColors(category);
  
  return {
    background: categoryColors.bg,
    color: categoryColors.color,
    border: `1px solid ${categoryColors.border}`,
    fontSize: '0.75rem',
    fontWeight: 500,
  };
});

const EventTypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'eventType',
})<{ eventType: EventType }>(({ eventType }) => {
  const getColors = (type: EventType) => {
    switch (type) {
      case 'Conference': return { bg: 'rgba(255, 87, 34, 0.2)', color: '#ff7043', border: '#ff7043' };
      case 'Seminar': return { bg: 'rgba(63, 81, 181, 0.2)', color: '#5c6bc0', border: '#5c6bc0' };
      case 'Workshop': return { bg: 'rgba(0, 150, 136, 0.2)', color: '#26a69a', border: '#26a69a' };
      case 'Networking': return { bg: 'rgba(233, 30, 99, 0.2)', color: '#ec407a', border: '#ec407a' };
      case 'Presentation': return { bg: 'rgba(121, 85, 72, 0.2)', color: '#8d6e63', border: '#8d6e63' };
      default: return { bg: 'rgba(158, 158, 158, 0.2)', color: '#bdbdbd', border: '#bdbdbd' };
    }
  };
  
  const typeColors = getColors(eventType);
  
  return {
    background: typeColors.bg,
    color: typeColors.color,
    border: `1px solid ${typeColors.border}`,
    fontSize: '0.75rem',
    fontWeight: 500,
  };
});

const StatsContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '24px',
  marginBottom: '32px',
  justifyContent: 'center',
  flexWrap: 'wrap',
}));

const StatCard = styled(Paper)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    padding: '16px',
    textAlign: 'center',
    minWidth: '120px',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease',
    },
  };
});

const ThemedTabs = styled(Tabs)(() => {
  const colors = getActiveColorTheme();
  return {
    '& .MuiTab-root': {
      color: colors.text.disabled,
      fontWeight: 600,
      fontSize: '1rem',
      '&.Mui-selected': {
        color: colors.brand.accent.main,
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: colors.brand.accent.main,
    },
  };
});

const ThemedTextField = styled(TextField)(() => {
  const colors = getActiveColorTheme();
  return {
    '& .MuiOutlinedInput-root': {
      color: colors.text.secondary,
      '& fieldset': {
        borderColor: colors.border.primary,
      },
      '&:hover fieldset': {
        borderColor: colors.border.accent,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.border.accent,
      },
    },
  };
});

const ThemedSelect = styled(Select)(() => {
  const colors = getActiveColorTheme();
  return {
    color: colors.text.secondary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border.primary,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border.accent,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border.accent,
    },
  };
});

const ThemedPagination = styled(Pagination)(() => {
  const colors = getActiveColorTheme();
  return {
    '& .MuiPaginationItem-root': {
      color: colors.text.secondary,
      borderColor: colors.border.primary,
      '&:hover': {
        backgroundColor: colors.effects.glow,
        borderColor: colors.border.accent,
      },
      '&.Mui-selected': {
        backgroundColor: colors.brand.accent.main,
        color: colors.brand.accent.contrastText,
        '&:hover': {
          backgroundColor: colors.brand.accent.dark,
        },
      },
    },
  };
});

const PrimaryButton = styled(Button)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.primary,
    color: colors.brand.accent.contrastText,
    fontWeight: 600,
    '&:hover': {
      background: colors.gradients.primary,
      filter: 'brightness(1.1)',
    }
  };
});

const SecondaryButton = styled(Button)(() => {
  const colors = getActiveColorTheme();
  return {
    color: colors.brand.accent.main,
    borderColor: colors.border.accent,
    '&:hover': {
      background: colors.effects.glow,
      borderColor: colors.border.accent,
    }
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface NewsItemProps {
  article: NewsArticle;
  onClick: NewsArticleClickHandler;
}

const renderNewsImage = (article: NewsArticle) => {
  if (!article.featuredImage) return null;

  // Detect if this might be a logo/badge based on file name or aspect ratio
  const imageUrl = getMediaUrl(article.featuredImage);
  const isLikelyLogo = imageUrl.toLowerCase().includes('logo') || 
                      imageUrl.toLowerCase().includes('badge') || 
                      imageUrl.toLowerCase().includes('shield') ||
                      imageUrl.toLowerCase().includes('crest');

  const imageStyle = isLikelyLogo ? {
    objectFit: 'contain' as const,
    objectPosition: 'center' as const,
    padding: '16px',
  } : {
    objectFit: 'cover' as const,
    objectPosition: 'center' as const,
  };

  const containerStyle = isLikelyLogo ? {
    backgroundColor: '#1A1A1A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } : {};

  return (
    <CardMedia 
      sx={{ 
        position: 'relative', 
        height: 200,
        overflow: 'hidden',
        borderTopLeftRadius: 'inherit',
        borderTopRightRadius: 'inherit',
        ...containerStyle,
      }}
    >
      <Image
        src={imageUrl}
        alt={article.title}
        fill
        style={imageStyle}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        unoptimized={true}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 1,
          zIndex: 2,
        }}
      >
        <CategoryChip
          label={article.category}
          size="small"
          category={article.category}
        />
        {article.externalLink && (
          <Chip
            icon={<OpenInNew sx={{ fontSize: '0.8rem' }} />}
            label="External"
            size="small"
            sx={{
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '0.7rem',
            }}
          />
        )}
      </Box>
    </CardMedia>
  );
};

const NewsItem = React.memo<NewsItemProps>(({ article, onClick }) => {
  const colors = getActiveColorTheme();
  
  const handleClick = useCallback(() => {
    onClick(article);
  }, [article, onClick]);

  return (
    <NewsCard onClick={handleClick}>
      {renderNewsImage(article)}
      
      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h6"
          sx={{
            color: colors.brand.accent.main,
            fontWeight: 600,
            marginBottom: 1,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.title}
        </Typography>
        
        {article.excerpt && (
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              lineHeight: 1.6,
              marginBottom: 2,
              flexGrow: 1,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {article.excerpt}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              {formatDate(article.publishedDate)}
            </Typography>
          </Box>
          
          {article.author && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                {article.author.attributes?.name || 'Anonymous'}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </NewsCard>
  );
});

NewsItem.displayName = 'NewsItem';

interface EventItemProps {
  event: Event;
  isPast: boolean;
  onClick: (event: Event) => void;
}

const EventItem = React.memo<EventItemProps>(({ event, isPast, onClick }) => {
  const colors = getActiveColorTheme();
  
  const handleClick = useCallback(() => {
    onClick(event);
  }, [event, onClick]);

  return (
    <EventCard 
      onClick={handleClick}
      className={isPast ? 'past-event' : ''}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1, mr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: isPast ? colors.text.disabled : colors.brand.accent.main,
                fontWeight: 600,
                marginBottom: 1,
                lineHeight: 1.3,
              }}
            >
              {event.title}
            </Typography>
            
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary,
                lineHeight: 1.6,
                marginBottom: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {event.description}
            </Typography>
          </Box>
          
          <EventTypeChip
            label={event.eventType}
            size="small"
            eventType={event.eventType}
          />
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              {formatDate(event.startDateTime)}
              {event.endDateTime && ` - ${formatDate(event.endDateTime)}`}
            </Typography>
          </Box>
          
          {event.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                {event.isVirtual ? 'Virtual' : event.location}
              </Typography>
            </Box>
          )}
          
          {event.speakers && event.speakers.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                {event.speakers.length} Speaker{event.speakers.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
          
          {event.capacity && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Visibility sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
              <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                {event.capacity} capacity
              </Typography>
            </Box>
          )}
          
          {event.registrationLink && !isPast && (
            <SecondaryButton
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(event.registrationLink, '_blank', 'noopener,noreferrer');
              }}
              sx={{
                fontSize: '0.75rem',
                ml: 'auto',
              }}
            >
              Register
            </SecondaryButton>
          )}
        </Box>
      </CardContent>
    </EventCard>
  );
});

EventItem.displayName = 'EventItem';

const LoadingSkeleton = React.memo<{ type: 'news' | 'events' }>(({ type }) => {
  const colors = getActiveColorTheme();
  
  return (
    <>
      {Array.from({ length: type === 'news' ? 6 : 4 }, (_, index) => (
        <Grid key={index} size={{ xs: 12, sm: 6, md: type === 'news' ? 4 : 12 }}>
          <Card sx={{ 
            background: colors.gradients.card,
            border: `1px solid ${colors.border.primary}`,
            height: type === 'news' ? 350 : 150,
          }}>
            <Skeleton 
              variant="rectangular" 
              height={type === 'news' ? 200 : 0} 
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
            />
            <CardContent sx={{ p: 3 }}>
              <Skeleton 
                variant="text" 
                height={24} 
                width="80%" 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)', mb: 1 }} 
              />
              <Skeleton 
                variant="text" 
                height={16} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 1 }} 
              />
              <Skeleton 
                variant="text" 
                height={16} 
                width="60%" 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
              />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const NewsEventsPage: React.FC = () => {
  const colors = getActiveColorTheme();
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<NewsCategory | ''>('');
  const [selectedEventType, setSelectedEventType] = useState<EventType | ''>('');
  
  // Router for single page
  const router = useRouter(); 

  // Pagination
  const [newsPage, setNewsPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const itemsPerPage = 9;

  // API data - Now using real events API
  const { articles: allNews, loading: newsLoading, error: newsError } = useNewsArticles();
  const { events: allEvents, loading: eventsLoading, error: eventsError } = useEvents();

  // Filter and pagination logic
  const filteredNews = useMemo(() => {
    return allNews.filter(article => {
      const matchesSearch = searchQuery === '' || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === '' || article.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [allNews, searchQuery, selectedCategory]);

  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      const matchesSearch = searchQuery === '' || 
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = selectedEventType === '' || event.eventType === selectedEventType;
      
      return matchesSearch && matchesType;
    });
  }, [allEvents, searchQuery, selectedEventType]);

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = new Date();
    const upcoming = filteredEvents.filter(event => new Date(event.startDateTime) > now);
    const past = filteredEvents.filter(event => new Date(event.startDateTime) <= now);
    
    return {
      upcomingEvents: upcoming.sort((a, b) => 
        new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      ),
      pastEvents: past.sort((a, b) => 
        new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime()
      )
    };
  }, [filteredEvents]);

  // Paginated data
  const paginatedNews = useMemo(() => {
    const startIndex = (newsPage - 1) * itemsPerPage;
    return filteredNews.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredNews, newsPage]);

  const totalNewsPages = Math.ceil(filteredNews.length / itemsPerPage);

  // Event handlers
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedEventType('');
  }, []);

  const handleNewsClick = useCallback<NewsArticleClickHandler>((article) => {
    if (article.externalLink) {
      window.open(article.externalLink, '_blank', 'noopener,noreferrer');
    } else {
      // Navigate to the single news page using documentId
      router.push(`/news/${article.documentId}`);
    }
  }, [router]);

  const handleEventClick = useCallback((event: Event) => {
    // Navigate to the single event page using documentId
    router.push(`/events/${event.documentId}`);
  }, [router]);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <HeroContent>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                marginBottom: 2,
                background: colors.gradients.secondary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              News & Events
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                fontWeight: 400,
                color: colors.text.secondary,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              Stay updated with the latest announcements, research developments, and upcoming events
            </Typography>
          </HeroContent>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Statistics */}
        <StatsContainer>
          <StatCard>
            <ArticleIcon sx={{ color: colors.brand.accent.main, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.main, fontWeight: 700 }}>
              {allNews.length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              News Articles
            </Typography>
          </StatCard>
          
          <StatCard>
            <EventIcon sx={{ color: colors.brand.accent.main, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.main, fontWeight: 700 }}>
              {upcomingEvents.length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Upcoming Events
            </Typography>
          </StatCard>
          
          <StatCard>
            <AccessTime sx={{ color: colors.brand.accent.main, fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.main, fontWeight: 700 }}>
              {pastEvents.length}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              Past Events
            </Typography>
          </StatCard>
        </StatsContainer>

        {/* Tabs */}
        <Paper sx={{ 
          mb: 4, 
          background: colors.gradients.card, 
          border: `1px solid ${colors.border.secondary}` 
        }}>
          <ThemedTabs
            value={currentTab}
            onChange={handleTabChange}
            centered
          >
            <Tab 
              icon={<ArticleIcon />} 
              label="News & Announcements" 
              iconPosition="start" 
            />
            <Tab 
              icon={<EventIcon />} 
              label="Events & Workshops" 
              iconPosition="start" 
            />
          </ThemedTabs>
        </Paper>

        {/* Filters */}
        <FilterContainer>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <ThemedTextField
                fullWidth
                placeholder={currentTab === 0 ? "Search news articles..." : "Search events..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.brand.accent.main }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              {currentTab === 0 ? (
                <FormControl fullWidth>
                  <InputLabel sx={{ color: colors.text.disabled }}>Category</InputLabel>
                  <ThemedSelect
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value as NewsCategory | '')}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="News">News</MenuItem>
                    <MenuItem value="Announcement">Announcements</MenuItem>
                    <MenuItem value="Media Coverage">Media Coverage</MenuItem>
                    <MenuItem value="Newsletter">Newsletters</MenuItem>
                  </ThemedSelect>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel sx={{ color: colors.text.disabled }}>Event Type</InputLabel>
                  <ThemedSelect
                    value={selectedEventType}
                    onChange={(e) => setSelectedEventType(e.target.value as EventType | '')}
                    label="Event Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="Conference">Conferences</MenuItem>
                    <MenuItem value="Seminar">Seminars</MenuItem>
                    <MenuItem value="Workshop">Workshops</MenuItem>
                    <MenuItem value="Networking">Networking</MenuItem>
                    <MenuItem value="Guest Lecture">Guest Lectures</MenuItem>
                  </ThemedSelect>
                </FormControl>
              )}
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <SecondaryButton
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                  setSelectedEventType('');
                  setNewsPage(1);
                  setEventsPage(1);
                }}
                sx={{ py: 1.5 }}
              >
                Clear Filters
              </SecondaryButton>
            </Grid>
          </Grid>
        </FilterContainer>

        {/* Content */}
        {currentTab === 0 ? (
          // News Content
          <Box>
            {newsLoading ? (
              <Grid container spacing={3}>
                <LoadingSkeleton type="news" />
              </Grid>
            ) : newsError ? (
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: colors.gradients.card,
                  border: `1px solid ${colors.border.primary}`,
                }}
              >
                <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>
                  Unable to Load News Articles
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                  {newsError}
                </Typography>
              </Paper>
            ) : paginatedNews.length > 0 ? (
              <>
                <Grid container spacing={3}>
                  {paginatedNews.map((article) => (
                    <Grid key={article.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <NewsItem article={article} onClick={handleNewsClick} />
                    </Grid>
                  ))}
                </Grid>
                
                {totalNewsPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <ThemedPagination
                      count={totalNewsPages}
                      page={newsPage}
                      onChange={(e, page) => setNewsPage(page)}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: colors.gradients.card,
                  border: `1px solid ${colors.border.primary}`,
                }}
              >
                <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
                  No News Articles Found
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                  {searchQuery || selectedCategory
                    ? 'Try adjusting your search criteria or clearing filters.'
                    : 'News articles will appear here once they are published.'}
                </Typography>
              </Paper>
            )}
          </Box>
        ) : (
          // Events Content
          <Box>
            {eventsLoading ? (
              <Grid container spacing={3}>
                <LoadingSkeleton type="events" />
              </Grid>
            ) : eventsError ? (
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: colors.gradients.card,
                  border: `1px solid ${colors.border.primary}`,
                }}
              >
                <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>
                  Unable to Load Events
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                  {eventsError}
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Upcoming Events */}
                {upcomingEvents.length > 0 && (
                  <Box sx={{ mb: 6 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: colors.brand.accent.main,
                        fontWeight: 600,
                        marginBottom: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <EventIcon />
                      Upcoming Events
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {upcomingEvents.map((event) => (
                        <Grid key={event.id} size={{ xs: 12 }}>
                          <EventItem 
                            event={event} 
                            isPast={false} 
                            onClick={handleEventClick} 
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {/* Past Events */}
                {pastEvents.length > 0 && (
                  <Box>
                    <Divider sx={{ borderColor: colors.border.primary, mb: 4 }} />
                    
                    <Typography
                      variant="h4"
                      sx={{
                        color: colors.text.disabled,
                        fontWeight: 600,
                        marginBottom: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <AccessTime />
                      Past Events
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {pastEvents.map((event) => (
                        <Grid key={event.id} size={{ xs: 12 }}>
                          <EventItem 
                            event={event} 
                            isPast={true} 
                            onClick={handleEventClick} 
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {upcomingEvents.length === 0 && pastEvents.length === 0 && (
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      background: colors.gradients.card,
                      border: `1px solid ${colors.border.primary}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
                      No Events Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                      {searchQuery || selectedEventType
                        ? 'Try adjusting your search criteria or clearing filters.'
                        : 'Events will appear here once they are scheduled.'}
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </Box>
        )}

        {/* Newsletter Signup CTA */}
        <Paper
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            background: colors.gradients.card,
            border: `1px solid ${colors.border.accent}`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.brand.accent.main,
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Stay Connected
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              marginBottom: 3,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Subscribe to our newsletter for the latest updates on research developments, 
            upcoming events, and program announcements delivered directly to your inbox.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <PrimaryButton
              variant="contained"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
              }}
            >
              Subscribe to Newsletter
            </PrimaryButton>
            
            <SecondaryButton
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
              }}
            >
              Contact Us
            </SecondaryButton>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default NewsEventsPage;